import React from 'react'
import PropTypes from 'prop-types'
import { SimpleInput } from 'components/Input'
import { isEmpty } from 'tools/string'

const LEADING_ZEROS_FORMAT = /^(-)?0+(?=\d)/
const THREE_DIGITS_FORMAT = /\B(?=(\d{3})+(?!\d))/g
const NUMERIC_FORMAT = /^-?\d*\.{0,1}\d*$/

const NumberInput = ({
  Input,
  onChange,
  name,
  value,
  controlled,
  allowDecimal,
  allowNegative,
  allowLeadingZeros,
  autoFormat,
  maxDigit,
  decimalPlaces,
  ...props
}) => {
  const [numberValue, setNumberValue] = React.useState(value)

  React.useEffect(() => {
    if (!isEmpty(value)) {
      const nextValue = getNextValue(value)
      if (nextValue !== undefined) {
        setNumberValue(nextValue)
      } else {
        setNumberValue('')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getNextValue = (_value) => {
    // trim leading and trailing spaces and remove comma from auto-formatted value
    // auto-formatted value is applied when autoFormat props is evaluated to true
    let value = _value.toString().trim().replace(/,/g, '')

    if (!NUMERIC_FORMAT.test(value)) {
      return
    }

    // will not allow preceeding characters to go through if the length of the given string has exceeded the maxDigit value
    // maxDigit does not count period and dash
    if (maxDigit && value.replace(/\.|-/g, '').length > maxDigit) {
      return
    }

    if (!allowNegative && value.includes('-')) {
      return
    }

    // will not allow period character to go through if allowDecimal props is evaluated to false
    if (!allowDecimal && value.includes('.')) {
      return
    }

    // will not allow preceeding characters to go through if the length of the given string after the period has exceeded the decimalPlaces value
    if (decimalPlaces && (value.split('.')[1] || []).length > decimalPlaces) {
      return
    }

    // remove leading zeros if allowLeadingZeros props is evaluated to false
    if (!allowLeadingZeros) {
      value = value.replace(LEADING_ZEROS_FORMAT, '$1')
    }

    // cluster integers by a group of 3 if autoFormat props is evaluated to true, rule does not apply to decimal
    // 123456 -> 1,234,456
    if (autoFormat) {
      const tempValue = value.split('.')
      const integer = tempValue[0]
      const decimal = tempValue[1]
      value = `${integer.replace(THREE_DIGITS_FORMAT, ',')}${decimal ? `.${decimal}` : `${value.includes('.') ? '.' : ''}`}`
    }

    return value || ''
  }

  const onInputChange = (name, value) => {
    const nextValue = getNextValue(value)

    if (nextValue !== undefined) {
      if (!controlled) {
        setNumberValue(nextValue)
      }

      if (typeof onChange === 'function') {
        onChange(name, nextValue)
      }
    }
  }

  // TODO: Find a way to display number keypad on iOS
  return (
    <Input
      {...props}
      inputMode='numeric'
      name={name}
      value={controlled ? value : numberValue}
      onChange={onInputChange}
      controlled />
  )
}

NumberInput.propTypes = {
  Input: PropTypes.element,
  onChange: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  controlled: PropTypes.bool,
  allowDecimal: PropTypes.bool,
  allowNegative: PropTypes.bool,
  allowLeadingZeros: PropTypes.bool,
  autoFormat: PropTypes.bool, // -12345.6 -> -12,345.6
  maxDigit: PropTypes.number,
  decimalPlaces: PropTypes.number
}

NumberInput.defaultProps = {
  Input: SimpleInput,
  controlled: false,
  allowDecimal: false,
  allowNegative: false,
  allowLeadingZeros: false,
  autoFormat: false
}

export default NumberInput
